import { CheckCircleOutlined, ClockCircleOutlined, StopOutlined } from '@ant-design/icons'
import { RateUpdateInfo } from '@components/Freight/Ratebooks/Drill-in/components/RateUpdateInfo'
import { BBDTag, Horizontal, MaterialIcon } from '@gravitate-js/excalibrr'
import { Popover, Tooltip } from 'antd'
import dayjs from 'dayjs'
import type * as React from 'react'

/**
 * Generic interface that works for different rate types
 * - ManualAccessorialRate
 * - SurchargeRate
 * - Rate (from PointToPoint)
 */
export interface BaseRateType {
  id?: string
  effective_from: string
  effective_to?: string | null
  created_by?: string
  created_on?: string
  updated_by?: string | string[]
  updated_on?: string
  updated_date?: string
  created_date?: string
}

export interface RateStatusTagProps<T extends BaseRateType> {
  rate: T
  isMostRecentActive?: boolean
  /** Optional status label to display next to the icon */
  showStatusLabel?: boolean
  /** Optional callback to determine if a rate is active based on custom logic */
  customStatusDetermination?: (rate: T, isMostRecentActive?: boolean) => 'active' | 'scheduled' | 'expired'
  /** Optional callback to set audit order ID */
  setAuditOrderId?: (id: string) => void
}

/**
 * Unified RateStatusTag component to display rate status across different rate types
 */
export function RateStatusTag<T extends BaseRateType>({
  rate,
  isMostRecentActive = false,
  showStatusLabel = false,
  customStatusDetermination,
}: RateStatusTagProps<T>) {
  const now = dayjs()
  const effectiveFrom = dayjs(rate.effective_from as string)
  const effectiveTo = rate.effective_to ? dayjs(rate.effective_to) : null

  // Determine status
  let status: 'active' | 'scheduled' | 'expired'

  if (customStatusDetermination) {
    // Use custom status determination if provided
    status = customStatusDetermination(rate, isMostRecentActive)
  } else {
    // Default status determination logic
    if (effectiveFrom.isAfter(now)) {
      // If the rate's effective date is in the future, it's scheduled
      status = 'scheduled'
    } else if (effectiveTo?.isBefore(now)) {
      // If the rate has an end date and it's in the past, it's expired
      status = 'expired'
    } else if (!isMostRecentActive) {
      // If the rate is not the most recent active rate, it's expired
      status = 'expired'
    } else {
      // Only the most recent active rate that's not scheduled or expired is active
      status = 'active'
    }
  }

  // Set icon and tooltip text based on status
  let icon: React.ReactNode
  let tooltipText = ''

  switch (status) {
    case 'active':
      icon = <CheckCircleOutlined />
      tooltipText = 'This rate is currently active'
      break
    case 'scheduled':
      icon = <ClockCircleOutlined />
      tooltipText = `This rate will become active on ${effectiveFrom.format('MM/DD/YYYY')}`
      break
    case 'expired':
      icon = <StopOutlined />
      tooltipText = 'This rate is no longer active'
      if (effectiveTo) {
        tooltipText = `This rate expired on ${effectiveTo.format('MM/DD/YYYY')}`
      }
      break
    default:
      icon = <CheckCircleOutlined />
      tooltipText = 'This rate is currently active'
      break
  }

  return (
    <Horizontal gap={8} alignItems='center' style={{ overflow: 'visible' }}>
      {/* Status tag with icon */}
      <Tooltip title={tooltipText}>
        <BBDTag
          theme2={status === 'active'}
          warning={status === 'scheduled'}
          style={{
            width: 'fit-content',
            textTransform: 'capitalize',
          }}
        >
          {icon} {showStatusLabel && status}
        </BBDTag>
      </Tooltip>

      {/* Update information icon with popover - show for all rates */}
      <Popover content={<RateUpdateInfo rate={rate} />} title='Rate Update History' trigger='hover' placement='left'>
        <BBDTag
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            height: '100%',
          }}
        >
          <MaterialIcon icon='history' size={14} />
        </BBDTag>
      </Popover>
    </Horizontal>
  )
}

/**
 * Helper function to determine if a rate is currently active
 */
export function isRateActive<T extends BaseRateType>(rate: T, isMostRecentActive?: boolean): boolean {
  const now = dayjs()
  const effectiveFrom = dayjs(rate.effective_from)
  const effectiveTo = rate.effective_to ? dayjs(rate.effective_to) : null

  // A rate is active if:
  // 1. It's effective from a date in the past or today
  // 2. It doesn't have an end date or the end date is in the future
  // 3. It's marked as the most recent active rate
  return (
    (effectiveFrom.isBefore(now) || effectiveFrom.isSame(now, 'day')) &&
    (!effectiveTo || effectiveTo.isAfter(now)) &&
    !!isMostRecentActive
  )
}
